import type { InputProps } from '@chakra-ui/react'
import { FormErrorMessage, forwardRef, Input, InputGroup, InputLeftElement } from '@chakra-ui/react'
import { customAddDays } from 'BrokerTsLib/src/extensions/date.extensions'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { dateTimeFormat, formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { Czech } from 'flatpickr/dist/l10n/cs'
import type { BaseOptions } from 'flatpickr/dist/types/options'
import moment from 'moment'
import type { ChangeEventHandler } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Flatpickr from 'react-flatpickr'

import { Calendar } from '../icons'

interface DatepickerProps extends InputProps {
  date?: Date | null
  onDateChange: (date?: Date) => void
  isOpen?: boolean
  options?: Partial<BaseOptions>
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined
  errors?: FieldError | undefined
  onClose?: Function,
  showIcon?: boolean,
  htmlSize?: number
}

/**
 * @deprecated stejny uz s CC v2. Spojit do brokerTsLib
 */
export const Datepicker = forwardRef((props: DatepickerProps, ref) => {
  const { options, date: originalDate, isOpen = false, onClose, errors, isDisabled, showIcon = true, htmlSize = 8 } = props
  const { minDate, disable, } = options || {}
  const fp = useRef<Flatpickr>(null)

  // useMemo kvuli zmene data v ramci formulare, napr. nastaveni data 1 registrace z VinOnline
  const oDate = useMemo(() => !originalDate || originalDate as any == '' ? undefined : new Date(originalDate), [originalDate])
  const [date, setDate] = useState(oDate)

  useEffect(() => {
    setDate(oDate)
  }, [oDate])

  const handleDateChange = (date?: Date) => {
    const d = date && moment(date).isValid() ? new Date(date) : undefined

    props.onDateChange(d)
    setDate(d)
  }

  useEffect(() => {
    if (isOpen) {
      fp?.current?.flatpickr?.open()
      fp?.current?.flatpickr?.input?.focus()
    }
    else
      fp?.current?.flatpickr?.close()
    return () => { }
  }, [isOpen])

  const errorMessage = errors && errors?.message

  useEffect(() => {
    if (minDate) {
      if (moment(date ?? new Date()).isBefore(moment(minDate))) {
        const from = customAddDays(date ?? new Date(), 1)
        const fromSelectedDateToMinDay = {
          from: formatDate(from, dateTimeFormat),
          to: formatDate(customAddDays(minDate as Date, -1), dateTimeFormat),
        }

        const disableFromSelectedToMin = [fromSelectedDateToMinDay]

        // If selected date is before minDate, set minDate to selected date
        fp.current?.flatpickr.set('minDate', date ?? from)
        // Disable all dates after selected date and before minDate
        fp.current?.flatpickr.set('disable', [...(disable ?? []), ...disableFromSelectedToMin])
        // Redraw flatpickr
        fp.current?.flatpickr.redraw()
      } else {
        fp.current?.flatpickr.set('minDate', minDate)
        fp.current?.flatpickr.set('disable', [{
          to: formatDate(customAddDays(minDate as Date, -1), dateTimeFormat),
        }
        ])
      }
    }

    return () => { }
  }, [minDate, date, disable])

  return (
    <>
      <Flatpickr
        ref={fp}
        onBlur={(e) => {
          if (!fp.current?.flatpickr.isOpen) {
            const inputVal = fp.current?.flatpickr.input.value
            handleDateChange(!inputVal || !moment(inputVal, dateTimeFormat).isValid() ? undefined : moment(inputVal, dateTimeFormat).toDate())
          }

          // onBlur?.(e)
        }}
        options={{
          ...options,
          minDate: undefined,
          // allowInvalidPreload: true, // pokud je true, tak to pada na invalid date (napr kdyz se zada string)
          locale: Czech,
          animate: true,
          dateFormat: options?.enableTime ? 'd.m.Y H:i' : 'd.m.Y',
          allowInput: true,
          onClose: () => onClose?.(),
        }}
        value={date}
        render={({ defaultValue, onBlur }, ref) => {
          return (
            <InputGroup>
              {showIcon && <InputLeftElement
                pointerEvents="none"
                children={
                  <Calendar width={4} height={4} />
                }
              />}

              <Input
                defaultValue={defaultValue}
                ref={ref}
                htmlSize={htmlSize}
                isDisabled={isDisabled}
                onBlur={(e) => {
                  onBlur?.(e)
                }}
              />

            </InputGroup>
          )
        }}
        onChange={([date]) => handleDateChange(date)}
      />

      {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </>
  )
})